import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { Mic, Square, AlertCircle, WifiOff } from "lucide-react";
import { mixpanel } from "../../../../utils/mixpanel";
import {
  beginPatientAudioUpload,
  startAudioProcessing,
  uploadAudio,
} from "../../../../services/api-documentation";

import { retryFailedVoiceNotes } from "./retryFailedVoiceNotes";

import MP3EncoderService from "../../../../services/mp3encoder";
import LocalStorageService from "../../../../services/localstorage";
import { SessionContext } from "../../../../contexts";
import wakeLockService from "../../../../utils/wakeLock";
import { logUIError } from "../../../../services/api-misc";

// Onboarding tips imports
import { RainbowBorder } from "../../../common/native";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../utils/constant";
import EncounterTypeModal from "./encounter-type/EncounterTypeModal";
import { useEncounterTypes } from "./encounter-type/useEncounterTypes";
import RecordingRecoveryPanel from "./voice-recording/components/RecordingRecoveryPanel";
import { useChunkedRecorder } from "./voice-recording/hooks/useChunkedRecorder";
import { useRecordingRecovery } from "./voice-recording/hooks/useRecordingRecovery";
import { RecordingUploadMode } from "./voice-recording/types/recording";

const getTodayLocalISODate = (): string => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const normalizeNoteDate = (value?: string | null): string | null => {
  if (!value) return null;

  const trimmedValue = value.trim();
  const isoDateMatch = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const tzOffsetMs = parsedDate.getTimezoneOffset() * 60000;
  return new Date(parsedDate.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const getDefaultNoteDate = (consultationDate?: string | null): string => {
  const today = getTodayLocalISODate();
  const appointmentDate = normalizeNoteDate(consultationDate);

  if (!appointmentDate) {
    return today;
  }

  return appointmentDate <= today ? appointmentDate : today;
};

interface VoiceRecorderProps {
  patient: {
    id: string;
    name: string;
    patientRecordId?: string;
    email?: string;
    phone?: string;
    appointmentId?: number | null;
    consultationDate?: string | null;
    clinicOSUUID?: string;
  };
  clinicId: string;
  onSaveRecording: (result: {
    transcription: string;
    summary: string;
    audioUrl: string;
    audioId: number;
    noteType: string;
  }) => void;
  session?: {
    userEmail?: string;
    clinicName?: string;
    accessToken?: string;
  };
  tipsEnabled?: boolean;
  appointmentId?: number | null;
  autoStart?: boolean;
  onAutoStartHandled?: () => void;
}

type RecordingType = string | null;

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  patient,
  clinicId,
  onSaveRecording,
  tipsEnabled = false,
  appointmentId,
  autoStart = false,
  onAutoStartHandled,
}) => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingType, setRecordingType] = useState<RecordingType>(null);
  const [showTypeModal, setShowTypeModal] = useState<boolean>(false);
  const defaultNoteDate = useMemo(
    () => getDefaultNoteDate(patient.consultationDate),
    [patient.consultationDate],
  );
  const [noteDate, setNoteDate] = useState<string>(defaultNoteDate);
  const [noteTypeError, setNoteTypeError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isPreparingToRecord, setIsPreparingToRecord] =
    useState<boolean>(false);
  const [recorderError, setError] = useState<string | null>(null);
  const [recordingUploadMode, setRecordingUploadMode] =
    useState<RecordingUploadMode>("chunked_resumable");
  const [processingStep, setProcessingStep] = useState<string>("");
  const [hasAudioData, setHasAudioData] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // New state for MP3 encoding
  const [isEncodingMP3, setIsEncodingMP3] = useState<boolean>(false);
  const [encodingProgress, setEncodingProgress] = useState<number>(0);
  const [isSafari, setIsSafari] = useState<boolean>(false);
  const [wakeLockUnavailable, setWakeLockUnavailable] =
    useState<boolean>(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopRequestedRef = useRef<boolean>(false);
  const wakeLockReleaseRef = useRef<(() => Promise<void>) | null>(null);
  const autoStartTriggeredRef = useRef<boolean>(false);

  const { tipsState, markStepComplete } = useOnboardingTips();

  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;
  const maxNoteDate = useMemo(() => getTodayLocalISODate(), []);
  const appointmentIdToUse =
    appointmentId ?? patient.appointmentId ?? undefined;
  const isChunkedMode = recordingUploadMode === "chunked_resumable";

  useEffect(() => {
    setNoteDate(defaultNoteDate);
  }, [defaultNoteDate, appointmentIdToUse, patient.clinicOSUUID]);

  const logVoiceUIError = async (
    message: string,
    extraContext?: Record<string, unknown>,
  ) => {
    try {
      await logUIError({
        message,
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "VoiceRecorder",
          patientId: patient?.clinicOSUUID,
          appointmentId: appointmentIdToUse,
          ...extraContext,
        },
      });
    } catch (err) {
      console.warn("logVoiceUIError failed", err);
    }
  };

  const {
    available,
    loading,
    error: typesError,
    createType,
  } = useEncounterTypes(session);

  const {
    isRecording: isChunkedRecording,
    isProcessing: isChunkedProcessing,
    processingStep: chunkedProcessingStep,
    startRecording: startChunkedRecording,
    stopRecording: stopChunkedRecording,
  } = useChunkedRecorder({
    session: session ?? null,
    clinicId,
    patientId: patient.clinicOSUUID || "",
    patientName: patient.name,
    onCompleted: onSaveRecording,
    onError: (message) => setError(message),
  });

  const {
    pendingRecoveryCount,
    isRecovering,
    recover: recoverChunkedRecordings,
  } = useRecordingRecovery({
    session: session ?? null,
    clinicId,
    patientId: patient.clinicOSUUID || "",
    onRecovered: onSaveRecording,
  });

  const isAnyRecording = isRecording || isChunkedRecording;
  const isAnyProcessing = isProcessing || isChunkedProcessing;
  const activeProcessingStep = isChunkedProcessing
    ? chunkedProcessingStep
    : processingStep;

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Detect Safari for known MediaRecorder 60s auto-stop behavior
  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isSafariBrowser =
      /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Edge/i.test(ua);
    setIsSafari(isSafariBrowser);
  }, []);

  useEffect(() => {
    if (isSafari) {
      // Safari MediaRecorder chunked output is unreliable; force standard upload.
      setRecordingUploadMode("single_blob");
    }
  }, [isSafari]);

  // Timer effect
  useEffect(() => {
    // let timerId: ReturnType<typeof setInterval> | null = null;
    let timerId: number | null = null;

    if (isAnyRecording) {
      timerId = window.setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);

      timerRef.current = timerId;
    }

    return () => {
      if (timerId !== null) {
        window.clearInterval(timerId);
      }
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);

        timerRef.current = null;
      }
    };
  }, [isAnyRecording]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      } else {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      }

      if (wakeLockReleaseRef.current) {
        wakeLockReleaseRef.current();
        wakeLockReleaseRef.current = null;
      }
    };
  }, []);

  // Retry failed recordings when coming back online
  useEffect(() => {
    if (!isOnline || !session?.accessToken) return;

    const handler = setTimeout(() => {
      console.log("🔄 Retrying failed recordings...");
      retryFailedVoiceNotes({
        session: session,
        onSaveRecording,
      });
    }, 1000); // wait for network to stabilize

    return () => clearTimeout(handler);
  }, [isOnline, session?.accessToken]);

  useEffect(() => {
    if (!autoStart || autoStartTriggeredRef.current) {
      return;
    }
    autoStartTriggeredRef.current = true;
    onAutoStartHandled?.();
    handleStartRecording();
  }, [autoStart, onAutoStartHandled]);

  const ensurePatientLinked = (): boolean => {
    if (patient.clinicOSUUID) {
      return true;
    }

    // No ClinicOS UUID means this appointment isn't linked to a ClinicOS patient
    // record. Surface that instead of trying to resolve it from other identifiers.
    setError("This patient isn't linked to a ClinicOS record.");

    mixpanel.track("Voice Recording Error", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient?.clinicOSUUID,
      "Patient Name": patient?.name,
      "Error Message": "Patient not linked to a ClinicOS record",
      "Error Stage": "patient_creation",
    });
    logVoiceUIError("Voice recording blocked: patient missing ClinicOS UUID", {
      stage: "patient_creation",
      noteType: recordingType,
    });

    return false;
  };

  const handleStartRecording = async (): Promise<void> => {
    console.log("🎙️ Record button pressed – preparing to start recording");
    setError(null);
    setIsPreparingToRecord(true);
    stopRequestedRef.current = false;
    setWakeLockUnavailable(false);

    try {
      // Check MP3 encoding support
      if (!MP3EncoderService.isSupported()) {
        setError(
          "Your browser doesn't support the required audio features for recording",
        );
        setIsPreparingToRecord(false);
        return;
      }

      const patientLinked = ensurePatientLinked();

      if (!patientLinked) {
        setIsPreparingToRecord(false);
        return;
      }

      setNoteTypeError(null);
      setShowTypeModal(true);

      mixpanel.track("Voice Recording Type Selection Opened", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
      });
    } catch (recorderError) {
      setError("Failed to start recording");

      mixpanel.track("Voice Recording Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Error Message":
          recorderError instanceof Error
            ? recorderError.message
            : "Unknown error",
        "Error Stage": "prepare_recording",
      });
      logVoiceUIError("Voice recording failed during preparation", {
        stage: "prepare_recording",
        noteType: recordingType,
        error:
          recorderError instanceof Error
            ? recorderError.message
            : String(recorderError),
      });
    } finally {
      setIsPreparingToRecord(false);
    }
  };

  const startRecording = async (typeParam?: RecordingType): Promise<void> => {
    try {
      if (typeof window !== "undefined" && window.stopVoiceNotesAudio) {
        window.stopVoiceNotesAudio();
      }

      audioChunksRef.current = [];
      setHasAudioData(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Use WebM with Opus codec for better quality, then convert to MP3
      const options = {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 128000,
      };

      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, options);
      } catch (err) {
        // Fallback to default options
        mediaRecorder = new MediaRecorder(stream);
      }

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          setHasAudioData(true);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsRecording(false);
        setHasAudioData(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (wakeLockReleaseRef.current) {
          await wakeLockReleaseRef.current();
          wakeLockReleaseRef.current = null;
        }

        if (!stopRequestedRef.current) {
          mixpanel.track("Voice Recording Unexpectedly Stopped", {
            "User Email": session?.userEmail,
            "Clinic Name": session?.clinicName,
            "Patient ID": patient?.clinicOSUUID,
            "Patient Name": patient?.name,
            "Note Type": typeParam || recordingType,
            "Recording Duration": recordingTime,
            "Device Type": navigator.userAgent || "Unknown",
            Browser: isSafari ? "Safari" : "Other",
          });
        }

        if (audioChunksRef.current.length === 0) {
          setError("No audio data captured. Please try again.");
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        // Convert to MP3
        await convertAndProcessRecording(audioBlob, typeParam || recordingType);

        mixpanel.track("Voice Recording Completed", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient?.clinicOSUUID,
          "Patient Name": patient?.name,
          "Recording Duration": recordingTime,
          "Audio Size (bytes)": audioBlob.size,
          "Note Type": typeParam || recordingType,
        });
      };

      // Try to keep the screen awake on supported browsers (Chrome/Safari new versions)
      console.log("⏯️ Attempting wake lock request from VoiceRecorder...");
      const release = await wakeLockService.requestScreenWakeLock();
      wakeLockReleaseRef.current = release;
      if (!release) {
        console.log("⚠️ Wake lock unavailable or denied");
        setWakeLockUnavailable(true);
      }

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);

      setRecordingTime(0);
      setIsRecording(true);
      stopRequestedRef.current = false;

      mixpanel.track("Voice Recording Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Device Type": navigator.userAgent || "Unknown",
        "Has ClinicOS UUID": !!patient.clinicOSUUID,
        "Note Type": recordingType,
      });
    } catch (recorderError) {
      setError(
        "Error accessing microphone. Please check permissions and try again.",
      );

      mixpanel.track("Voice Recording Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Error Message":
          recorderError instanceof Error
            ? recorderError.message
            : "Unknown error",
        "Error Stage": "start_recording",
        "Note Type": recordingType,
      });
      logVoiceUIError("Voice recording failed to start", {
        stage: "start_recording",
        noteType: recordingType,
        error:
          recorderError instanceof Error
            ? recorderError.message
            : String(recorderError),
      });
    }
  };

  const stopStandardRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      stopRequestedRef.current = true;
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (wakeLockReleaseRef.current) {
        wakeLockReleaseRef.current();
        wakeLockReleaseRef.current = null;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const stopRecording = (): void => {
    if (isChunkedMode) {
      stopChunkedRecording();
      return;
    }

    stopStandardRecording();
  };

  const convertAndProcessRecording = async (
    blob: Blob,
    typeOverride?: RecordingType,
  ): Promise<void> => {
    const typeToUseRaw = typeOverride ?? recordingType;
    const typeToUse =
      typeof typeToUseRaw === "string" ? typeToUseRaw.trim() : "";

    if (!typeToUse || !blob) {
      setError("Note type not selected. Please try recording again.");
      return;
    }

    setIsProcessing(true);
    setProcessingStep("Converting to MP3...");
    setIsEncodingMP3(true);

    try {
      if (!patient.clinicOSUUID) {
        throw new Error("Patient UUID is missing. Cannot save recording.");
      }
      if (!session) {
        throw new Error("Missing authentication session for processing audio.");
      }

      // --- Step 1: Convert to MP3 (or fall back to original) ---
      let mp3Blob: Blob;
      try {
        const validation = MP3EncoderService.validateAudioBlob(blob);
        if (!validation.isValid) throw new Error(validation.error);

        setEncodingProgress(25);
        mp3Blob = await MP3EncoderService.convertToMP3(blob, {
          sampleRate: 44100,
          bitRate: 128,
          channels: 1,
        });
        setEncodingProgress(100);

        mixpanel.track("Voice Recording MP3 Conversion Success", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient?.clinicOSUUID,
          "Original Size (bytes)": blob.size,
          "MP3 Size (bytes)": mp3Blob.size,
          "Compression Ratio": Math.round((1 - mp3Blob.size / blob.size) * 100),
        });
      } catch (encodingError) {
        console.error(
          "MP3 encoding failed, using original audio:",
          encodingError,
        );
        mp3Blob = blob;

        const encodingErrorMessage =
          encodingError instanceof Error
            ? encodingError.message
            : "Unknown error";
        mixpanel.track("Voice Recording MP3 Conversion Failed", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient?.clinicOSUUID,
          Error: encodingErrorMessage,
        });
        logVoiceUIError(
          "Voice recording MP3 conversion failed; using fallback",
          {
            stage: "mp3_conversion",
            noteType: typeToUse,
            error: encodingErrorMessage,
          },
        );
      }

      setIsEncodingMP3(false);

      // --- Step 2: Upload (phase 1 UI) ---
      setProcessingStep("Uploading audio...");
      let audioId: number;
      let getUrl: string;
      try {
        const {
          audioId: id,
          putUrl,
          getUrl: url,
        } = await beginPatientAudioUpload(
          session,
          patient.clinicOSUUID,
          "surgeon_audio",
          noteDate || undefined,
          { source: "VoiceRecorder.tsx" },
        );
        audioId = id;
        getUrl = url;

        await uploadAudio(mp3Blob, putUrl, {
          session,
          patientId: patient.clinicOSUUID,
          audioId,
          source: "VoiceRecorder.tsx",
        });
      } catch (uploadErr: any) {
        // Save locally for retry, then surface a friendly message
        await LocalStorageService.storeRecording({
          audioBlob: mp3Blob,
          metadata: {
            patientId: patient.id,
            patientClinicOSUUID: patient.clinicOSUUID || undefined,
            patientName: patient.name,
            noteType: typeToUse,
            clinicId,
            userEmail: session?.userEmail ?? undefined,
            clinicName: session?.clinicName ?? undefined,
            timestamp: new Date().toISOString(),
            noteDateOverride: noteDate || undefined,
          },
        });

        setError(
          "You are offline. Voice note saved and will upload automatically.",
        );
        mixpanel.track("Voice Note Stored Locally", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient?.clinicOSUUID,
          "Patient Name": patient?.name,
          "Note Type": typeToUse,
          Reason:
            uploadErr instanceof Error ? uploadErr.message : "Upload error",
        });

        // Clean up UI state and exit early
        setAudioBlob(null);
        setRecordingTime(0);
        setRecordingType(null);
        setIsProcessing(false);
        setProcessingStep("");
        return;
      }

      // --- Step 3: Processing (phase 2 UI) ---
      setProcessingStep("Processing… you can come back later.");
      const { summary, transcription } = await startAudioProcessing(
        session,
        audioId!,
        patient.clinicOSUUID,
        typeToUse,
        appointmentIdToUse,
        noteDate || undefined,
      );

      onSaveRecording({
        transcription,
        summary,
        audioUrl: getUrl!,
        audioId: audioId!,
        noteType: typeToUse,
      });

      mixpanel.track("Voice Note Saved Successfully", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Note Type": typeToUse,
        "Has Transcription": !!transcription,
        "Has Summary": !!summary,
        "Audio ID": audioId!,
        "ClinicOS UUID": patient.clinicOSUUID,
        "Audio Format": "MP3",
      });

      // --- Cleanup ---
      setAudioBlob(null);
      setRecordingTime(0);
      setRecordingType(null);
      setIsProcessing(false);
      setProcessingStep("");
    } catch (error) {
      setError("Failed to process voice note. Please try again.");

      mixpanel.track("Voice Note Processing Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient?.clinicOSUUID,
        "Patient Name": patient?.name,
        "Note Type": typeToUse,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
        "ClinicOS UUID": patient.clinicOSUUID || "Missing",
      });

      logVoiceUIError("Voice note processing error", {
        noteType: typeToUse,
        error: error instanceof Error ? error.message : String(error),
      });

      setIsProcessing(false);
      setIsEncodingMP3(false);
      setProcessingStep("");
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <View style={styles.container}>
      <RecordingRecoveryPanel
        pendingCount={pendingRecoveryCount}
        isRecovering={isRecovering}
        onRetry={recoverChunkedRecordings}
      />

      {/* Online/Offline indicator */}
      {!isOnline && (
        <View style={styles.offlineIndicator}>
          <WifiOff size={16} color="#E53935" />
          <Text style={styles.offlineText}>
            Offline - recordings will be saved locally and uploaded when
            connection is restored
          </Text>
        </View>
      )}

      {/* Safari warning shown only while recording */}
      {isSafari && isAnyRecording && (
        <View style={styles.warningContainer}>
          <Text style={styles.warningText}>
            Keep this tab active while recording audio so your computer does not
            go to sleep!
          </Text>
        </View>
      )}

      {/* Wake lock unavailable/denied warning */}
      {wakeLockUnavailable && (
        <View style={styles.warningContainer}>
          <Text style={styles.warningText}>
            Unable to prevent the screen from sleeping. Keep this tab active and
            your screen awake during recording.
          </Text>
        </View>
      )}

      {/* Error message display */}
      {recorderError && (
        <View style={styles.errorContainer}>
          <AlertCircle size={20} color="#E53935" />
          <Text style={styles.errorText}>{recorderError}</Text>
        </View>
      )}
      {!isAnyRecording &&
      !audioBlob &&
      !isPreparingToRecord &&
      !isAnyProcessing ? (
        <RainbowBorder
          isActive={
            tipsEnabled &&
            tipsState[TOOLTIP_STEPS.SELECT_APPOINTMENT] &&
            !tipsState[TOOLTIP_STEPS.RECORD_CONSULT]
          }
          borderRadius={12}
          style={{ alignSelf: "flex-start" }}
        >
          <TouchableOpacity
            style={styles.recordButton}
            onPress={() => {
              markStepComplete(TOOLTIP_STEPS.RECORD_CONSULT);
              handleStartRecording();
            }}
          >
            <Mic size={24} color="#FFFFFF" />
            <Text style={styles.recordButtonText}>Record Voice Note</Text>
          </TouchableOpacity>
        </RainbowBorder>
      ) : isPreparingToRecord ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#0085FF" />
          <Text style={styles.loadingText}>Preparing to record...</Text>
        </View>
      ) : isAnyRecording ? (
        <View style={styles.recordingContainer}>
          <View style={styles.recordingContent}>
            <Text style={styles.recordingText}>
              Recording... {formatTime(recordingTime)}
            </Text>
            <View style={styles.recordingStatusContainer}>
              <View
                style={[
                  styles.recordingStatusDot,
                  hasAudioData
                    ? styles.recordingStatusDotActive
                    : styles.recordingStatusDotInactive,
                ]}
              />
              <Text style={styles.recordingStatusText}>
                {isChunkedRecording
                  ? "Recording…"
                  : hasAudioData
                    ? "Capturing audio…"
                    : "Waiting for microphone…"}
              </Text>
            </View>
          </View>
          <TouchableOpacity style={styles.stopButton} onPress={stopRecording}>
            <Square size={20} color="#FFFFFF" />
            <Text style={styles.stopButtonText}>Stop</Text>
          </TouchableOpacity>
        </View>
      ) : isAnyProcessing ? (
        <View style={styles.processingContainer}>
          <ActivityIndicator size="small" color="#0085FF" />
          <View style={styles.processingContent}>
            <Text style={styles.processingText}>{activeProcessingStep}</Text>
            {isEncodingMP3 && (
              <View style={styles.progressContainer}>
                <View
                  style={[
                    styles.progressBar,
                    { width: `${encodingProgress}%` },
                  ]}
                />
              </View>
            )}
          </View>
        </View>
      ) : null}

      {/* Recording Type Modal */}
      <EncounterTypeModal
        visible={showTypeModal}
        onClose={() => {
          setShowTypeModal(false);
          setNoteTypeError(null);
        }}
        onSelect={async (type, selectedDate) => {
          setShowTypeModal(false);
          const normalizedType = typeof type === "string" ? type.trim() : "";
          if (!normalizedType) {
            setNoteTypeError("Please choose a note type.");
            return;
          }
          const normalizedDate = selectedDate || getTodayLocalISODate();
          setNoteDate(normalizedDate);
          setRecordingType(normalizedType);
          if (isChunkedMode) {
            await startChunkedRecording({
              noteType: normalizedType,
              noteDate: normalizedDate,
              appointmentId: appointmentIdToUse,
            });
          } else {
            await startRecording(normalizedType);
          }
        }}
        loading={loading}
        error={noteTypeError || typesError || undefined}
        types={available}
        allowCreate
        onCreateType={async (name: string) => {
          const created = await createType(name);
          // optional: analytics here
        }}
        noteDate={noteDate}
        maxDate={maxNoteDate}
        onChangeNoteDate={(value) => setNoteDate(value)}
        disableNoteDate={
          isAnyRecording || isAnyProcessing || isPreparingToRecord
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    marginBottom: 4,
  },
  offlineIndicator: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF3E0",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  offlineText: {
    color: "#E65100",
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
  },
  warningContainer: {
    backgroundColor: "#FFF8E1",
    borderColor: "#FFB300",
    borderWidth: 1,
    padding: 10,
    borderRadius: 8,
    marginBottom: 12,
  },
  warningText: {
    color: "#FF6F00",
    fontSize: 12,
    lineHeight: 16,
  },
  recordButton: {
    backgroundColor: "#00B7F4",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignSelf: "flex-start",
    gap: 10,
  },
  recordButtonText: {
    color: "#FFFFFF",
    fontFamily: "Urbanist500",
    textAlign: "center",
    fontSize: 16,
    lineHeight: 22.4,
    letterSpacing: 0.032,
  },
  recordingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFE5E5",
    padding: 16,
    borderRadius: 8,
  },
  recordingText: {
    color: "#E53935",
    fontSize: 16,
    fontWeight: "500",
  },
  recordingContent: {
    flex: 1,
  },
  recordingStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 6,
  },
  recordingStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  recordingStatusDotActive: {
    backgroundColor: "#4CAF50",
  },
  recordingStatusDotInactive: {
    backgroundColor: "#FF9800",
  },
  recordingStatusText: {
    color: "#D32F2F",
    fontSize: 12,
  },
  stopButton: {
    backgroundColor: "#E53935",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  stopButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 6,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    padding: 16,
    borderRadius: 8,
  },
  loadingText: {
    color: "#555555",
    fontSize: 16,
    fontWeight: "500",
    marginLeft: 12,
  },
  processingContainer: {
    flexDirection: "row",
    backgroundColor: "#E3F2FD",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  processingContent: {
    flex: 1,
    marginLeft: 12,
  },
  processingText: {
    color: "#0085FF",
    fontSize: 16,
    fontWeight: "500",
  },
  progressContainer: {
    height: 4,
    backgroundColor: "#BBDEFB",
    borderRadius: 2,
    marginTop: 8,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#0085FF",
    borderRadius: 2,
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFEBEE",
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  errorText: {
    color: "#E53935",
    fontSize: 14,
    marginLeft: 8,
  },
});

export default VoiceRecorder;
